import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../core/sdk';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Card } from 'primereact/card';
import { Tag } from 'primereact/tag';
import { Splitter, SplitterPanel } from 'primereact/splitter';
import { Panel } from 'primereact/panel';
import { InputTextarea } from 'primereact/inputtextarea';
import { Toolbar } from 'primereact/toolbar';
import { Tooltip } from 'primereact/tooltip';
import DocumentAdminStore, { DocumentTemplateStore } from '../stores/DocumentAdminStore';
import { getAllSignatureTemplates, SignatureTemplate } from '../config/signatureTemplates';

const TemplateEditor = observer(
    ({
        template,
        store,
        sdk,
    }: {
        template: DocumentTemplateStore;
        store: DocumentAdminStore;
        sdk: ApplicationSdk;
    }) => {
        const [htmlContent, setHtmlContent] = useState(template.html_content);
        const [hasChanges, setHasChanges] = useState(false);
        const signatureTemplates = getAllSignatureTemplates();

        useEffect(() => {
            setHtmlContent(template.html_content);
            setHasChanges(false);
        }, [template.id, template.html_content]);

        const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            setHtmlContent(e.target.value);
            setHasChanges(e.target.value !== template.html_content);
        };

        const insertSignatureTemplate = (signatureTemplate: SignatureTemplate) => {
            const textarea = document.querySelector('textarea') as HTMLTextAreaElement;
            if (textarea) {
                const cursorPosition = textarea.selectionStart;
                const textBefore = htmlContent.substring(0, cursorPosition);
                const textAfter = htmlContent.substring(cursorPosition);
                const newContent =
                    textBefore + '\n\n' + signatureTemplate.html + '\n\n' + textAfter;

                setHtmlContent(newContent);
                setHasChanges(newContent !== template.html_content);

                // Focus back to textarea
                setTimeout(() => {
                    textarea.focus();
                    const newCursorPosition = cursorPosition + signatureTemplate.html.length + 4;
                    textarea.setSelectionRange(newCursorPosition, newCursorPosition);
                }, 10);
            }
        };

        const handleSave = async () => {
            const result = await store.saveTemplateContent(template.id, htmlContent);
            if (result.success) {
                sdk.showSuccessToast('Template erfolgreich gespeichert');
                setHasChanges(false);
            } else {
                sdk.showErrorToast(result.error || 'Fehler beim Speichern');
            }
        };

        const handleReset = () => {
            setHtmlContent(template.html_content);
            setHasChanges(false);
        };

        return (
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {/* Header with save/reset buttons */}
                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '1rem',
                        padding: '0.5rem 0',
                    }}
                >
                    <h3 style={{ margin: 0, color: '#333' }}>{template.name} - HTML Editor</h3>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {hasChanges && (
                            <Button
                                label="Zurücksetzen"
                                icon="pi pi-refresh"
                                onClick={handleReset}
                                className="p-button-outlined p-button-secondary"
                                size="small"
                            />
                        )}
                        <Button
                            label="Speichern"
                            icon="pi pi-save"
                            onClick={handleSave}
                            disabled={!hasChanges}
                            className="p-button-success"
                            size="small"
                        />
                    </div>
                </div>

                {/* Signature Templates Toolbar */}
                <div style={{ marginBottom: '1rem' }}>
                    <Toolbar
                        start={
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{ fontWeight: 'bold', color: '#666' }}>
                                    Signatur-Vorlagen:
                                </span>
                                {signatureTemplates.map(signatureTemplate => (
                                    <div key={signatureTemplate.id}>
                                        <Button
                                            id={`signature-btn-${signatureTemplate.id}`}
                                            label={signatureTemplate.name}
                                            icon="pi pi-plus"
                                            onClick={() =>
                                                insertSignatureTemplate(signatureTemplate)
                                            }
                                            className="p-button-outlined p-button-sm"
                                            size="small"
                                            style={{ fontSize: '12px' }}
                                        />
                                        <Tooltip
                                            target={`#signature-btn-${signatureTemplate.id}`}
                                            content={signatureTemplate.description}
                                            position="top"
                                        />
                                    </div>
                                ))}
                            </div>
                        }
                        style={{
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #dee2e6',
                            borderRadius: '6px',
                            padding: '0.5rem 1rem',
                        }}
                    />
                </div>

                {/* Splitter for Editor and Preview */}
                <div style={{ flex: 1, minHeight: '500px' }}>
                    <Splitter style={{ height: '100%' }}>
                        <SplitterPanel size={50}>
                            <Panel
                                header="HTML Editor"
                                style={{ height: '100%', width: '100%' }}
                            >
                                <InputTextarea
                                    value={htmlContent}
                                    onChange={handleContentChange}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        minHeight: '400px',
                                        fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                                        fontSize: '14px',
                                    }}
                                    placeholder="HTML-Inhalt hier eingeben..."
                                    rows={20}
                                />
                            </Panel>
                        </SplitterPanel>
                        <SplitterPanel size={50}>
                            <Panel
                                header="Vorschau"
                                style={{ height: '100%', width: '100%' }}
                            >
                                <div
                                    style={{
                                        border: '1px solid #ddd',
                                        borderRadius: '4px',
                                        padding: '1rem',
                                        backgroundColor: '#fff',
                                        height: '100%',
                                        minHeight: '400px',
                                        overflow: 'auto',
                                    }}
                                    dangerouslySetInnerHTML={{ __html: htmlContent }}
                                />
                            </Panel>
                        </SplitterPanel>
                    </Splitter>
                </div>
            </div>
        );
    }
);

const TemplateCard = observer(
    ({
        template,
        store,
        sdk,
        onEdit,
    }: {
        template: DocumentTemplateStore;
        store: DocumentAdminStore;
        sdk: ApplicationSdk;
        onEdit: (template: DocumentTemplateStore) => void;
    }) => {
        const handleDelete = async () => {
            const result = await sdk.getDialogConfirm({
                title: 'Template löschen',
                message: `Sind Sie sicher, dass Sie das Template "${template.name}" löschen möchten?`,
            });

            if (result.confirmed) {
                const deleteResult = await store.deleteTemplate(template.id);
                if (deleteResult.success) {
                    sdk.showSuccessToast('Template erfolgreich gelöscht');
                } else {
                    sdk.showErrorToast(deleteResult.error || 'Fehler beim Löschen');
                }
            }
        };

        return (
            <Card
                style={{
                    marginBottom: '1rem',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                        }}
                    >
                        <div style={{ flex: 1 }}>
                            <h4 style={{ margin: '0 0 0.5rem 0', color: '#333' }}>
                                {template.name}
                            </h4>
                            <small style={{ color: '#999' }}>
                                Erstellt:{' '}
                                {new Date(template.created_at).toLocaleDateString('de-DE')}
                                {template.updated_at !== template.created_at && (
                                    <>
                                        {' '}
                                        | Aktualisiert:{' '}
                                        {new Date(template.updated_at).toLocaleDateString('de-DE')}
                                    </>
                                )}
                            </small>
                        </div>
                        <Tag
                            value="Template"
                            severity="info"
                            icon="pi pi-file-o"
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                        <Button
                            label="HTML bearbeiten"
                            icon="pi pi-code"
                            onClick={() => onEdit(template)}
                            className="p-button-primary"
                            size="small"
                        />
                        <Button
                            label="Löschen"
                            icon="pi pi-trash"
                            onClick={handleDelete}
                            className="p-button-outlined p-button-danger"
                            size="small"
                        />
                    </div>
                </div>
            </Card>
        );
    }
);

const TemplatesList = observer(
    ({
        store,
        sdk,
        onEdit,
    }: {
        store: DocumentAdminStore;
        sdk: ApplicationSdk;
        onEdit: (template: DocumentTemplateStore) => void;
    }) => {
        const handleCreateTemplate = async () => {
            const result = await sdk.getDialogMultiInput({
                title: 'Neues Template erstellen',
                fields: [
                    {
                        label: 'Template Name',
                        key: 'name',
                        type: 'text',
                        required: true,
                    },
                ],
                message: 'Erstellen Sie ein neues Dokument-Template.',
                onConfirm: async (values: any) => store.createTemplate(values.name),
            });

            if (result.confirmed) {
                sdk.showSuccessToast('Template erfolgreich erstellt');
            } else if (result.error) {
                sdk.showErrorToast(result.error);
            }
        };

        return (
            <Card
                title="Dokument-Templates"
                style={{
                    marginBottom: '1rem',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <div className="p-fluid">
                    <div style={{ marginBottom: '1.5rem', textAlign: 'right' }}>
                        <Button
                            label="Neues Template"
                            icon="pi pi-plus"
                            onClick={handleCreateTemplate}
                            className="p-button-success"
                        />
                    </div>

                    {!store.templates || store.templates.length === 0 ? (
                        <Message
                            severity="info"
                            text="Keine Templates verfügbar"
                            style={{ marginBottom: '1rem' }}
                        />
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            {store.templates.map(template => (
                                <TemplateCard
                                    key={template.id}
                                    template={template}
                                    store={store}
                                    sdk={sdk}
                                    onEdit={onEdit}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </Card>
        );
    }
);

const DocumentAdminView = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    const [selectedTemplate, setSelectedTemplate] = useState<DocumentTemplateStore | null>(null);

    useEffect(() => {
        sdk.useStore(DocumentAdminStore);
    }, [sdk]);

    const store = sdk.currentStore;

    if (!store || store.isLoading) {
        return (
            <div
                style={{
                    maxWidth: '1200px',
                    margin: '0 auto',
                    padding: '2rem',
                    minHeight: '100vh',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <ProgressSpinner style={{ display: 'block', margin: '2rem auto' }} />
            </div>
        );
    }

    if (selectedTemplate) {
        return (
            <div
                style={{
                    maxWidth: '1400px',
                    margin: '0 auto',
                    padding: '2rem',
                    minHeight: '100vh',
                }}
            >
                <div style={{ marginBottom: '1rem' }}>
                    <Button
                        label="Zurück zur Liste"
                        icon="pi pi-arrow-left"
                        onClick={() => setSelectedTemplate(null)}
                        className="p-button-outlined"
                    />
                </div>
                <TemplateEditor
                    template={selectedTemplate}
                    store={store}
                    sdk={sdk}
                />
            </div>
        );
    }

    return (
        <div
            style={{
                maxWidth: '1000px',
                margin: '0 auto',
                padding: '2rem',
                minHeight: '100vh',
            }}
        >
            {store.error && (
                <Message
                    severity="error"
                    text={store.error}
                    style={{ marginBottom: '1.5rem' }}
                />
            )}

            <div className="p-fluid">
                <TemplatesList
                    store={store}
                    sdk={sdk}
                    onEdit={setSelectedTemplate}
                />
            </div>
        </div>
    );
});

export default DocumentAdminView;
